import { useState, useEffect, useRef } from "react";
import { Modal, Button, Table } from "react-bootstrap";
import { FaTrash, FaPlus, FaEdit } from "react-icons/fa";
import { Skeleton } from "primereact/skeleton"; 
import VMService from "../../../../services/VMService";
import AlertService from "../../../../services/AlertService";
import ConfirmDialogBox from "../../../confirmDialog/ConfirmDialogBox";

const VmModal = ({ show, handleClose, userVMId, authContext }) => {
  const [vmList, setVmList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addNew, setAddNew] = useState(false);
  const [newVm, setNewVm] = useState({ vmName: "", vmIdentifier: "", connectionId: "" });
  const [editingId, setEditingId] = useState(null);
  const [editedVm, setEditedVm] = useState({});
  const submitControllerRef = useRef(null); 
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedVmId, setSelectedVmId] = useState(null);

useEffect(() => {
    const controller = new AbortController();

    setVmList([]);
    setAddNew(false);
    setNewVm({ vmName: "", vmIdentifier: "", connectionId: "" });
    setEditingId(null);
    setEditedVm({});
    setLoading(true);

    if (!userVMId) {
      setLoading(false);
      return;
    }

    const load = async () => {
      try {
        setLoading(true);
        const data = await VMService.getList(
          userVMId,
          authContext,
          controller.signal 
        );

        if (controller.signal.aborted) return; 

        if (data.isSuccess) {
          setVmList(data.data || []);
        } else {
          AlertService.error(data.errorMessage || "Failed to load VMs");
        }
      } catch (err) {
        if (err.name === "AbortError") return; 
        AlertService.error("Something went wrong");
      } finally {
        if (!controller.signal.aborted) setLoading(false); 
      }
    };

    load();

    return () => controller.abort(); 
  }, [userVMId, authContext]);

  const handleModalClose = () => {
    setAddNew(false);
    setNewVm({ vmName: "", vmIdentifier: "", connectionId: "" });
    setEditingId(null);
    setEditedVm({});
    handleClose();
  };

  const handleAddClick = () => {
    setAddNew(true);
    setEditingId(null);
  };

  const startEdit = (vm) => {
    setEditingId(vm.id);
    setEditedVm({
      vmName: vm.vmName,
      vmIdentifier: vm.vmIdentifier,
      connectionId: vm.connectionId
    });
    setAddNew(false);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditedVm({});
  };

  const cancelAdd = () => {
    setAddNew(false);
    setNewVm({ vmName: "", vmIdentifier: "", connectionId: "" });
  };

  const handleInputChange = (e, isEdit = false) => {
    const { name, value } = e.target;
    if (isEdit) {
      setEditedVm(prev => ({ ...prev, [name]: value }));
    } else {
      setNewVm(prev => ({ ...prev, [name]: value }));
    }
  };

// Component unmount
useEffect(() => {
    return () => {
      submitControllerRef.current?.abort(); 
    };
  }, []);

const handleSave = async () => {
    if (submitControllerRef.current) {
      submitControllerRef.current.abort(); 
    }
    const controller = new AbortController();
    submitControllerRef.current = controller;

    setLoading(true);

    const payload = {
      vmId: userVMId,
      ...(editingId ? { id: editingId } : {}),
      vmName: editingId ? editedVm.vmName : newVm.vmName,
      vmIdentifier: editingId ? editedVm.vmIdentifier : newVm.vmIdentifier,
      connectionId: editingId ? editedVm.connectionId : newVm.connectionId,
    };

    try {
      const result = await VMService.saveVM(
        payload,
        authContext,
        controller.signal 
      );

      if (controller.signal.aborted) return; 

      if (result.isSuccess) {
        const data = await VMService.getList(userVMId, authContext);
        if (data.isSuccess) setVmList(data.data || []);

        if (editingId) {
          setEditingId(null);
          setEditedVm({});
          AlertService.success("VM updated successfully!");
        } else {
          setAddNew(false);
          setNewVm({ vmName: "", vmIdentifier: "", connectionId: "" });
          AlertService.success("VM added successfully!");
        }
      } else {
        AlertService.error(result.errorMessage);
      }
    } catch (err) {
      if (err.name === "AbortError") return; 
    } finally {
      if (!controller.signal.aborted) setLoading(false); 
    }
  };

  const confirmDeleteVm = (id) => {
    setSelectedVmId(id);
    setShowDeleteDialog(true);
  };

  const handleDeleteVm = async () => {
    if (!selectedVmId) return;
    setLoading(true);
    const result = await VMService.removeList(selectedVmId, authContext);
    setLoading(false);
    setShowDeleteDialog(false);

    if (result.isSuccess) {
      setVmList(prev => prev.filter(vm => vm.id !== selectedVmId));
      AlertService.success("VM  details deleted successfully!");
    } else {
      AlertService.error(result.errorMessage);
    }
  };

  const isAnyActionActive = addNew || editingId !== null;

  return (
    <>
      <Modal show={show} onHide={handleModalClose} backdrop="static" size="xl" centered>
        <Modal.Header closeButton className="border-0 px-md-3">
          <div className="w-100 text-center">
            <Modal.Title>List of VM(s)</Modal.Title>
          </div>
        </Modal.Header>

        <Modal.Body className="px-md-4">
          <Table bordered responsive>
            <thead>
              <tr>
                <th  className="custom-input-color">VM Name</th>
                <th  className="custom-input-color">VM ID</th>
                <th  className="custom-input-color">Connection ID</th>
                <th className="text-center custom-input-color">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                //Skeleton Loading
                <>
                  {[...Array(2)].map((_, i) => (
                    <tr key={`skeleton-${i}`}>
                      <td className="custom-input-color"><Skeleton height="2rem" className="mb-2" /></td>
                      <td className="custom-input-color"><Skeleton height="2rem" className="mb-2" /></td>
                      <td className="custom-input-color"><Skeleton height="2rem" className="mb-2" /></td>
                      <td className="custom-input-color"><Skeleton height="2rem" className="mb-2" /></td>
                    </tr>
                  ))}
                </>
              ) : vmList.length === 0 && !addNew ? (
                <tr>
                  <td colSpan="4" className="text-center py-5">
                    <div className="text-muted">
                      <i className="pi pi-desktop" style={{ fontSize: "3.5rem", color: "#dee2e6" }}></i>
                      <div className="mt-3 fs-5 fw-medium">No VMs found</div>
                      <small className="text-secondary">
                        Click <strong>Add New VM</strong> to create one
                      </small>
                    </div>
                  </td>
                </tr>
              ) : (
                <>
                  {vmList.map((vm) => (
                    <tr key={vm.id}>
                      {editingId === vm.id ? (
                        <>
                          <td className="custom-input-color">
                            <input type="text" name="vmName" className="form-control custom-input-color" value={editedVm.vmName || ""} onChange={(e) => handleInputChange(e, true)} disabled={loading} />
                          </td>
                          <td className="custom-input-color">
                            <input type="text" name="vmIdentifier" className="form-control custom-input-color " value={editedVm.vmIdentifier || ""} onChange={(e) => handleInputChange(e, true)} disabled={loading} />
                          </td>
                          <td className="custom-input-color">
                            <input type="text" name="connectionId" className="form-control custom-input-color" value={editedVm.connectionId || ""} onChange={(e) => handleInputChange(e, true)} disabled={loading} />
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="custom-input-color">{vm.vmName}</td>
                          <td className="custom-input-color">{vm.vmIdentifier}</td>
                          <td className="custom-input-color">{vm.connectionId}</td>
                        </>
                      )}

                      <td className="text-center custom-input-color">
                        {editingId === vm.id ? (
                          <FaTrash className="text-danger" style={{ cursor: "pointer" }} onClick={() => confirmDeleteVm(vm.id)} title="Delete" />
                        ) : (
                          <>
                            <FaEdit className="text-success me-3" style={{ cursor: "pointer" }} onClick={() => startEdit(vm)} title="Edit" />
                            <FaTrash className="text-danger" style={{ cursor: "pointer" }} onClick={() => confirmDeleteVm(vm.id)} title="Delete" />
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                  {addNew && (
                    <tr>
                      <td>
                        <input type="text" name="vmName" placeholder="VM Name" className="form-control" value={newVm.vmName} onChange={handleInputChange} disabled={loading} />
                      </td>
                      <td>
                        <input type="text" name="vmIdentifier" placeholder="VM ID" className="form-control" value={newVm.vmIdentifier} onChange={handleInputChange} disabled={loading} />
                      </td>
                      <td>
                        <input type="text" name="connectionId" placeholder="Connection ID" className="form-control" value={newVm.connectionId} onChange={handleInputChange} disabled={loading} />
                      </td>
                      <td className="text-center">

                      </td>
                    </tr>
                  )}
                </>
              )}
            </tbody>
          </Table>
        </Modal.Body>

        <Modal.Footer className="border-0 gap-3 px-md-3 mb-2">
          {isAnyActionActive ? (
            <>
              <Button variant="outline-secondary" className="px-4 py-2" onClick={editingId ? cancelEdit : cancelAdd} disabled={loading}>
                Cancel
              </Button>
              <Button variant="primary" className="px-4 py-2" onClick={handleSave} disabled={loading}>
                {editingId ? "Update" : "Save"}
              </Button>
            </>
          ) : (
            <Button variant="primary" className="px-4 px-md-5 py-2" onClick={handleAddClick} disabled={loading}>
              <FaPlus className="me-1" /> Add New VM
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      <ConfirmDialogBox
        visible={showDeleteDialog}
        onHide={() => setShowDeleteDialog(false)}
        onConfirm={handleDeleteVm}
        title={<>Are you sure you want to delete this VM?</>}
        confirmLabel="Yes, Delete"
        cancelLabel="Cancel"
      />
    </>
  );
};

export default VmModal;
